import React, { ReactNode } from "react";
import styled from "styled-components";

export interface IAnimateResizeProps {
  children: ReactNode | ReactNode[],
  duration?: string, // default is 0.3s
  easing?:   "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear" | "step-start" | "step-end", // default is ease-in-out
  delay?:    string, // default is 0s
  throttleTimer?: number, // in ms, default 200
}

interface IAnimateResizeContainerProps {
  height?:   number,
  duration?: string,
  easing?:   "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear" | "step-start" | "step-end",
  delay?:    string,
}

const Container = styled.div<IAnimateResizeContainerProps>`
  height:     ${props => props.height+"px"};
  transition: ${props => `height ${props.duration || "0.3s"} ${props.easing || "ease-in-out"} ${props.delay || "0s"}`};
`;

/**
 * 
 * @param duration Transition duration. Default: 0.3s.
 * @param easing   Transition timing function. Default: ease-in-out.
 * @param delay    Transition delay. Default: 0s.
 * @param throttleTimer Throttles rerendering when children resize. Time in ms. Default: 200.
 */
export const AnimateResize = (props: IAnimateResizeProps): JSX.Element => {
  const ref = React.useRef<HTMLDivElement>();
  const [height, setHeight] = React.useState<number>(undefined);
  const throttleTimer = props.throttleTimer || 200; // in ms
  const throttle      = React.useRef<boolean>(false);
  React.useLayoutEffect(() => {
    setHeight(Math.round(ref.current.scrollHeight));
    const observer = new ResizeObserver(() => {
      if(!throttle.current){
        setHeight(Math.round(ref.current?.scrollHeight));
        throttle.current = true;
        setTimeout(() => {
          setHeight(Math.round(ref.current?.scrollHeight));
          throttle.current = false;
        }, throttleTimer);
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Container height={height} duration={props.duration} easing={props.easing} delay={props.delay}>
      <div ref={ref}>
        {props.children}
      </div>
    </Container>
  );
}